/**
 * Privacy Dashboard — incident KPIs, alert center, root cause trends.
 *
 * Modernized in Module 3: the dashboard now leads with the **alert center**
 * (overdue + critical/high/medium buckets from privacy_deadline_alerts) so
 * the compliance officer sees at-risk items the moment they open the app.
 */

import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_LABELS = {
  draft: 'Draft',
  under_investigation: 'Under investigation',
  risk_assessment_complete: 'Risk assessment complete',
  determination_pending_approval: 'Pending approval',
  approved_not_breach: 'Not a breach',
  approved_breach: 'Breach',
  notification_complete: 'Notification complete',
  closed: 'Closed'
};

const ROOT_CAUSE_LABELS = {
  human_error: 'Human error',
  system_failure: 'System failure',
  third_party: 'Third party',
  theft_loss: 'Theft/Loss',
  improper_disposal: 'Improper disposal',
  unknown: 'Unknown',
  other: 'Other'
};

const DEADLINE_TYPE_LABELS = {
  federal_hipaa: 'HIPAA federal (60-day)',
  state_individual: 'State individual notice',
  state_ag: 'State AG notification',
  state_media: 'State media notice'
};

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export default function PrivacyDashboard({ onOpenIncident }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/privacy/dashboard`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setStats(data);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const acknowledge = async (alertId) => {
    setAcking(alertId);
    try {
      await fetch(`${API}/api/privacy/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: '' })
      });
      await load();
    } catch (_) {}
    setAcking(null);
  };

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading dashboard...</div>;
  if (!stats) return <div className="p-8 text-slate-400 text-sm">Could not load dashboard.</div>;

  const alertStats = stats.alert_stats || { overdue: 0, critical: 0, high: 0, medium: 0, total: 0 };
  const recentAlerts = stats.recent_alerts || [];
  const hasAlerts = Number(alertStats.total) > 0;

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Privacy dashboard</h2>
          <p className="text-sm text-slate-500 mt-1">Real-time status across your incident portfolio.</p>
        </div>

        {/* ── Alert Center — hero ── */}
        {hasAlerts ? (
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-950 via-red-900 to-orange-900 text-white shadow-2xl shadow-red-900/20">
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'radial-gradient(circle at 10% 30%, white 1px, transparent 1px)',
              backgroundSize: '32px 32px'
            }} />
            <div className="relative p-7">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-red-200">Alert center</p>
                  <h3 className="text-xl font-bold">{Number(alertStats.total)} active deadline alert{Number(alertStats.total) === 1 ? '' : 's'}</h3>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <AlertBucket label="Overdue" value={alertStats.overdue} tone="danger" />
                <AlertBucket label="Critical (≤1 day)" value={alertStats.critical} tone="critical" />
                <AlertBucket label="High (≤7 days)" value={alertStats.high} tone="high" />
                <AlertBucket label="Medium (≤14 days)" value={alertStats.medium} tone="medium" />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-emerald-50 border border-emerald-200 p-6 flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-emerald-500 flex items-center justify-center text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald-700">Alert center</p>
              <h3 className="text-lg font-bold text-emerald-900">All clear</h3>
              <p className="text-sm text-emerald-700/80 mt-0.5">No deadlines within 14 days. Scanner runs hourly.</p>
            </div>
          </div>
        )}

        {/* Recent alerts list */}
        {recentAlerts.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Recent unacknowledged alerts</h3>
              <span className="text-xs text-slate-500">Scanner runs hourly</span>
            </div>
            <ul className="divide-y divide-slate-100">
              {recentAlerts.map((alert) => (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  onOpen={() => onOpenIncident(alert.incident_id)}
                  onAck={() => acknowledge(alert.id)}
                  isAcking={acking === alert.id}
                />
              ))}
            </ul>
          </div>
        )}

        {/* ── KPI cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Open incidents" value={stats.open_count ?? 0} />
          <KpiCard label="Breach" value={stats.breach_count ?? 0} tone="danger" />
          <KpiCard label="Not a breach" value={stats.non_breach_count ?? 0} tone="success" />
          <KpiCard
            label="By status"
            sub={
              Array.isArray(stats.by_status) && stats.by_status.length > 0
                ? stats.by_status.map((s) => `${STATUS_LABELS[s.status] || s.status}: ${s.count}`).join(' · ')
                : '—'
            }
          />
        </div>

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function AlertBucket({ label, value, tone }) {
  const toneClasses = {
    danger:   'bg-red-500/20 border-red-400/30 text-white',
    critical: 'bg-orange-500/20 border-orange-400/30 text-white',
    high:     'bg-amber-500/20 border-amber-400/30 text-white',
    medium:   'bg-yellow-500/20 border-yellow-400/30 text-white'
  }[tone] || 'bg-white/10 border-white/15 text-white';

  return (
    <div className={`rounded-2xl border backdrop-blur-sm px-4 py-3 ${toneClasses}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-90">{label}</p>
      <p className="text-3xl font-bold mt-0.5">{Number(value) || 0}</p>
    </div>
  );
}

function KpiCard({ label, value, sub, tone }) {
  const valueTone = {
    danger:  'text-red-600',
    success: 'text-emerald-600'
  }[tone] || 'text-slate-900';
  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5">
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
      {value != null ? (
        <p className={`text-3xl font-bold mt-1.5 ${valueTone}`}>{value}</p>
      ) : (
        <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{sub || '—'}</p>
      )}
    </div>
  );
}

function AlertRow({ alert, onOpen, onAck, isAcking }) {
  const urgencyTone = alert.threshold_days < 0
    ? 'bg-red-100 text-red-800 border-red-200'
    : alert.threshold_days <= 1
      ? 'bg-orange-100 text-orange-800 border-orange-200'
      : alert.threshold_days <= 7
        ? 'bg-amber-100 text-amber-800 border-amber-200'
        : 'bg-yellow-100 text-yellow-800 border-yellow-200';
  const urgencyLabel = alert.threshold_days < 0
    ? 'OVERDUE'
    : alert.threshold_days === 0
      ? 'Due today'
      : `${alert.threshold_days}d left`;

  const typeLabel = DEADLINE_TYPE_LABELS[alert.deadline_type] || alert.deadline_type;
  const stateTag = alert.state_code ? ` · ${alert.state_code}` : '';

  return (
    <li className="px-6 py-4 hover:bg-slate-50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${urgencyTone}`}>
              {urgencyLabel}
            </span>
            <span className="text-xs text-slate-500">{typeLabel}{stateTag}</span>
            <span className="text-xs text-slate-400">· {formatRelative(alert.alerted_at)}</span>
          </div>
          <button
            type="button"
            onClick={onOpen}
            className="mt-1 text-left font-semibold text-slate-900 hover:text-blue-600 transition-colors"
          >
            {alert.incident_title || 'Untitled incident'}
          </button>
          <p className="text-xs text-slate-500 mt-0.5">
            Deadline: {formatDate(alert.deadline_date)}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onOpen}
            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold hover:shadow-lg hover:shadow-blue-600/30 transition-all"
          >
            Open
          </button>
          <button
            type="button"
            onClick={onAck}
            disabled={isAcking}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            {isAcking ? 'Acking...' : 'Acknowledge'}
          </button>
        </div>
      </div>
    </li>
  );
}
